import React from "react";
import { ArrowsClockwiseIcon, XIcon } from "@phosphor-icons/react";
import { Modal, Platform, Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import { useTranslation } from "react-i18next";

import CustomText from "../../common/general/CustomText/CustomText";
import { colors } from "../../../styles/design-system";
import { getModalAnimationType } from "../../../utils/getModalAnimationType";

interface GapAnalysisResyncModalProps {
  visible: boolean;
  dismissedCount?: number;
  onShowDismissed?: () => void;
  onCancel: () => void;
  onResync: () => void;
  disableResync?: boolean;
  isResyncing?: boolean;
}

function GapAnalysisResyncModal({
  visible,
  dismissedCount = 0,
  onShowDismissed,
  onCancel,
  onResync,
  disableResync = false,
  isResyncing = false,
}: GapAnalysisResyncModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      transparent={true}
      visible={visible}
      animationType={getModalAnimationType("fade")}
      onRequestClose={onCancel}
      role="dialog"
      aria-label={t("text.gapAnalysisResyncModal.dialogAriaLabel")}
      aria-modal="true">
      <View style={styles.overlay}>
        <View
          style={styles.container}
          role="dialog"
          accessibilityLabel={t("text.gapAnalysisResyncModal.dialogAriaLabel")}>
          <View style={styles.header}>
            <View style={styles.headerRow}>
              <View style={styles.heroIconWrap}>
                <ArrowsClockwiseIcon size={22} color={colors.brand[600]} />
              </View>
              <View style={styles.headerTextWrap}>
                <CustomText
                  text="gapAnalysisResyncModal.title"
                  useTranslationText={true}
                  size="m"
                  weight="bold"
                  style={styles.title}
                />
                <CustomText
                  text="gapAnalysisResyncModal.subtitle"
                  useTranslationText={true}
                  size="xs"
                  weight="normal"
                  style={styles.subtitle}
                />
              </View>
            </View>

            <View style={styles.headerActions}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("text.gapAnalysisResyncModal.showDismissedAriaLabel")}
                onPress={onShowDismissed}
                style={(state) => [styles.showDismissedButton, getTextButtonInteractionStyle(state)]}>
                <CustomText
                  text="gapAnalysisResyncModal.showDismissed"
                  interpolation={{ count: dismissedCount }}
                  useTranslationText={true}
                  size="xs"
                  weight="medium"
                  style={styles.showDismissedText}
                />
              </Pressable>
            </View>
          </View>

          <View style={styles.body}>
            <CustomText
              text="gapAnalysisResyncModal.bodyLineOne"
              useTranslationText={true}
              size="s"
              weight="normal"
              style={styles.bodyText}
            />
            <CustomText
              text="gapAnalysisResyncModal.bodyLineTwo"
              useTranslationText={true}
              size="s"
              weight="normal"
              style={styles.bodyText}
            />
          </View>

          <View style={styles.footer}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("text.gapAnalysisResyncModal.cancelAriaLabel")}
              onPress={onCancel}
              style={(state) => [styles.cancelButton, getTextButtonInteractionStyle(state)]}>
              <XIcon size={13} color={colors.grey[700]} />
              <CustomText
                text="gapAnalysisResyncModal.cancel"
                useTranslationText={true}
                size="xs"
                weight="medium"
                style={styles.cancelText}
              />
            </Pressable>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("text.gapAnalysisResyncModal.resyncAriaLabel")}
              accessibilityState={{ disabled: disableResync || isResyncing }}
              disabled={disableResync || isResyncing}
              onPress={onResync}
              style={(state) => [styles.resyncButton, getBrandButtonInteractionStyle(state)]}>
              <ArrowsClockwiseIcon size={13} color={colors.white} />
              <CustomText
                text={isResyncing ? "gapAnalysisResyncModal.resyncing" : "gapAnalysisResyncModal.resync"}
                useTranslationText={true}
                size="xs"
                weight="medium"
                style={styles.resyncText}
              />
            </Pressable>
          </View>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("text.gapAnalysisResyncModal.closeAriaLabel")}
            onPress={onCancel}
            style={(state) => [styles.closeButton, getCloseButtonInteractionStyle(state)]}>
            <XIcon size={16} color={colors.grey[700]} />
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

function getTextButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: pressed ? colors.grey[200] : hovered ? colors.grey[100] : "transparent",
    transform: [{ scale: pressed ? 0.985 : hovered ? 1.015 : 1 }],
  };
}

function getBrandButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: pressed ? colors.brand[700] : hovered ? colors.brand[600] : colors.brand[500],
    transform: [{ scale: pressed ? 0.985 : hovered ? 1.015 : 1 }],
    opacity: pressed ? 0.95 : 1,
  };
}

function getCloseButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: hovered || pressed ? colors.grey[100] : "transparent",
    opacity: hovered ? 1 : 0.7,
  };
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(16, 24, 40, 0.42)",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  container: {
    width: "100%",
    maxWidth: 460,
    maxHeight: "90%",
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 10,
    overflow: "hidden",
    shadowColor: colors.grey[900],
    shadowOpacity: 0.2,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 12 },
    ...(Platform.OS === "web"
      ? ({
          transitionProperty: "opacity, transform",
          transitionDuration: "200ms",
          transitionTimingFunction: "ease-out",
        } as unknown as ViewStyle)
      : {}),
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 20,
    backgroundColor: colors.brand[25],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
    gap: 16,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  heroIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.brand[200],
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    color: colors.grey[900],
    lineHeight: 22,
  },
  subtitle: {
    marginTop: 2,
    color: colors.grey[600],
    lineHeight: 16,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  showDismissedButton: {
    minHeight: 24,
    borderRadius: 4,
    paddingHorizontal: 2,
    justifyContent: "center",
    ...(Platform.OS === "web"
      ? ({
          cursor: "pointer",
        } as unknown as ViewStyle)
      : {}),
  },
  showDismissedText: {
    color: colors.grey[600],
  },
  body: {
    paddingHorizontal: 24,
    paddingVertical: 20,
    gap: 12,
  },
  bodyText: {
    color: colors.grey[700],
    lineHeight: 21,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
    backgroundColor: colors.grey[50],
    paddingHorizontal: 24,
    paddingVertical: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 28,
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    ...(Platform.OS === "web"
      ? ({
          cursor: "pointer",
          transitionProperty: "transform, background-color",
          transitionDuration: "120ms",
          transitionTimingFunction: "ease-out",
        } as unknown as ViewStyle)
      : {}),
  },
  cancelText: {
    color: colors.grey[700],
  },
  resyncButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 28,
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: colors.brand[500],
    ...(Platform.OS === "web"
      ? ({
          cursor: "pointer",
          transitionProperty: "transform, background-color",
          transitionDuration: "120ms",
          transitionTimingFunction: "ease-out",
        } as unknown as ViewStyle)
      : {}),
  },
  resyncText: {
    color: colors.white,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.7,
    ...(Platform.OS === "web"
      ? ({
          cursor: "pointer",
          transitionProperty: "opacity, background-color",
          transitionDuration: "120ms",
          transitionTimingFunction: "ease-out",
        } as unknown as ViewStyle)
      : {}),
  },
});

export default GapAnalysisResyncModal;
